const QID_RE = /^Q\d+$/i;

/**
 * Prefer the linked inner span when legacy stacked mention+linked wrappers exist.
 *
 * @param {Element|null|undefined} entityEl
 * @returns {Element|null}
 */
export function normalizeWikidataEntityDomEl(entityEl) {
    if (!(entityEl instanceof Element)) return null;
    if (entityEl.matches('.wikidata-entity--linked')) return entityEl;
    const linked = entityEl.querySelector('.wikidata-entity--linked');
    if (linked instanceof Element) return linked;
    return entityEl.closest('.wikidata-entity') || entityEl;
}

/**
 * Whether two DOM nodes refer to the same wikidata mention span (including stacked
 * outer mention shell + inner linked layer).
 */
export function isSameEntityMentionElement(left, right) {
    if (!left || !right) return false;
    if (left === right) return true;
    if (left.contains(right) || right.contains(left)) return true;

    const leftRoot = left instanceof Element ? left.closest('.wikidata-entity') : null;
    const rightRoot = right instanceof Element ? right.closest('.wikidata-entity') : null;
    return Boolean(leftRoot && rightRoot && leftRoot === rightRoot);
}

/**
 * Suppress the lightweight hover tooltip when the detailed caret/click popover is already
 * open for the same wikidata *mention span* (not every span linked to the same QID).
 *
 * @param {object|null} visiblePayload - active caret-driven or click popover payload
 * @param {object|null} hoverTarget - hover tooltip target built from DOM attributes
 * @param {{markRange?: {from: number, to: number}|null}} [options]
 * @returns {boolean}
 */
export function shouldSuppressEntityHover(visiblePayload, hoverTarget, options = {}) {
    if (!visiblePayload || !hoverTarget) return false;
    if (visiblePayload.type !== 'wikidata') return false;

    const visibleAnchor = normalizeWikidataEntityDomEl(visiblePayload.anchorEl);
    const hoverAnchor = normalizeWikidataEntityDomEl(hoverTarget.anchorEl);
    if (isSameEntityMentionElement(visibleAnchor, hoverAnchor)) {
        return true;
    }

    const markRange = options.markRange;
    const hoverPos = hoverTarget.position;
    if (markRange && typeof hoverPos === 'number'
        && hoverPos >= markRange.from && hoverPos <= markRange.to) {
        return true;
    }

    const visiblePos = visiblePayload.position;
    if (typeof visiblePos === 'number' && typeof hoverPos === 'number' && visiblePos === hoverPos) {
        return true;
    }

    const visibleUid = typeof visiblePayload.uid === 'string' ? visiblePayload.uid.trim() : '';
    const hoverUid = typeof hoverTarget.uid === 'string' ? hoverTarget.uid.trim() : '';
    // Per-mark uids (wde-*) identify a single span. Bare QIDs are shared across aliases.
    if (visibleUid && hoverUid && visibleUid === hoverUid && !QID_RE.test(visibleUid)) {
        return true;
    }

    return false;
}

/**
 * Build the active-entity-popover payload consumed by {@link MentionHoverDismissProvider}.
 *
 * @param {{open?: boolean, anchorEl?: HTMLElement|null, uid?: string|null, position?: number|null}} entityDialog
 * @returns {object|null}
 */
export function buildActiveEntityPopover(entityDialog) {
    if (!entityDialog?.open) return null;
    return {
        type: 'wikidata',
        anchorEl: entityDialog.anchorEl || null,
        uid: entityDialog.uid || null,
        position: typeof entityDialog.position === 'number' ? entityDialog.position : null,
    };
}
